const nokFormatter = new Intl.NumberFormat('nb-NO', {
  style: 'currency',
  currency: 'NOK',
  maximumFractionDigits: 0,
});

const dateFormatter = new Intl.DateTimeFormat('nb-NO', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const shortDateFormatter = new Intl.DateTimeFormat('nb-NO', {
  day: 'numeric',
  month: 'short',
});

export function formatNok(value: number | null | undefined): string {
  if (value === null || value === undefined) return '–';
  return nokFormatter.format(value);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'Dato ikke satt';
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return 'Dato ikke satt';
  return dateFormatter.format(date);
}

/**
 * Kort dato. Tar både «2026-08-15» og «2026-08-15T13:16:24.781Z».
 *
 * Bryllupsdatoer kommer uten klokkeslett og må tolkes lokalt – legger man på
 * `T00:00:00` ikke, leses de som UTC og kan vises som dagen før. Tidsstempler
 * har allerede sonen med seg, og skal parses som de er. Uten skillet ble
 * `2026-08-15T13:16:24.781ZT00:00:00` til Invalid Date, og kolonnen sto tom.
 */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return shortDateFormatter.format(date);
}

/** Dager igjen til datoen. Negativt tall betyr at datoen har passert. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const target = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

export function initials(firstName: string, lastName?: string | null): string {
  const first = firstName.trim().charAt(0).toUpperCase();
  const last = (lastName ?? '').trim().charAt(0).toUpperCase();
  return `${first}${last}` || '?';
}

export function fullName(firstName: string, lastName?: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ').trim();
}

/** «3 gjester» / «1 gjest» */
export function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/**
 * «for 5 min siden». Brukes av bjella i begge båndene.
 *
 * Går over til dato etter en måned: da sier «for 47 dager siden» mindre enn
 * «12. juni», og lista blir lettere å lese.
 */
export function tidSiden(iso: string): string {
  const sekunder = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sekunder < 60) return 'nå nettopp';
  const minutter = Math.floor(sekunder / 60);
  if (minutter < 60) return `for ${minutter} min siden`;
  const timer = Math.floor(minutter / 60);
  if (timer < 24) return `for ${timer} ${timer === 1 ? 'time' : 'timer'} siden`;
  const dager = Math.floor(timer / 24);
  if (dager === 1) return 'i går';
  if (dager < 30) return `for ${dager} dager siden`;
  return new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long' });
}
